import { Phone, MapPin, Clock, Mail } from "lucide-react";
import {
    COMPANY,
    IMAGES,
    NEIGHBOR_CITIES,
    SERVICES,
    buildWhatsAppLink,
} from "../../lib/constants";

export const Footer = () => {
    const year = new Date().getFullYear();
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#0a0a0a] text-white pt-16 md:pt-20 pb-8 noise-bg relative"
        >
            <div className="emergency-stripes h-1.5 absolute top-0 left-0 right-0" />

            <div className="container mx-auto px-4 md:px-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
                    {/* Brand */}
                    <div className="lg:col-span-1">
                        <div className="flex items-center gap-3 mb-5">
                            <img
                                src={IMAGES.logo}
                                alt={`${COMPANY.name} logotipo`}
                                className="h-14 w-auto object-contain"
                            />
                            <div>
                                <div className="font-display font-extrabold text-lg uppercase tracking-tight">
                                    RCM Guincho
                                </div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-[#f45100] font-bold">
                                    Express · 24h
                                </div>
                            </div>
                        </div>
                        <p className="text-white/60 text-sm leading-relaxed">
                            Empresa especializada em guincho 24h, socorro
                            automotivo e reboque para todos os tipos de
                            veículos em Praia Grande/SP e região da Baixada
                            Santista.
                        </p>
                        <div className="mt-5 text-xs text-white/50">
                            <strong className="text-white/80">CNPJ:</strong>{" "}
                            {COMPANY.cnpj}
                        </div>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-[#f45100] mb-5">
                            Contato direto
                        </h4>
                        <ul className="space-y-4">
                            <li>
                                <a
                                    href={COMPANY.phoneTel}
                                    data-testid="footer-phone-link"
                                    className="flex items-start gap-3 group hover:text-[#f45100] transition-colors"
                                >
                                    <Phone
                                        size={18}
                                        className="mt-0.5 flex-shrink-0 text-[#f45100]"
                                        strokeWidth={2.5}
                                    />
                                    <div>
                                        <div className="text-xs text-white/50 uppercase tracking-wider">
                                            Ligação direta
                                        </div>
                                        <div className="font-bold text-base">
                                            {COMPANY.phone}
                                        </div>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a
                                    href={buildWhatsAppLink()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-whatsapp-link"
                                    className="flex items-start gap-3 group hover:text-[#25D366] transition-colors"
                                >
                                    <Mail
                                        size={18}
                                        className="mt-0.5 flex-shrink-0 text-[#25D366]"
                                        strokeWidth={2.5}
                                    />
                                    <div>
                                        <div className="text-xs text-white/50 uppercase tracking-wider">
                                            WhatsApp 24h
                                        </div>
                                        <div className="font-bold text-base">
                                            {COMPANY.phone}
                                        </div>
                                    </div>
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin
                                    size={18}
                                    className="mt-0.5 flex-shrink-0 text-[#f45100]"
                                    strokeWidth={2.5}
                                />
                                <div>
                                    <div className="text-xs text-white/50 uppercase tracking-wider">
                                        Atendimento
                                    </div>
                                    <div className="font-bold text-base">
                                        {COMPANY.region}
                                    </div>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Clock
                                    size={18}
                                    className="mt-0.5 flex-shrink-0 text-[#f45100]"
                                    strokeWidth={2.5}
                                />
                                <div>
                                    <div className="text-xs text-white/50 uppercase tracking-wider">
                                        Funcionamento
                                    </div>
                                    <div className="font-bold text-base">
                                        24 horas · 7 dias
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Services */}
                    <div>
                        <h4 className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-[#f45100] mb-5">
                            Nossos serviços
                        </h4>
                        <ul className="space-y-2.5">
                            {SERVICES.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={buildWhatsAppLink(s.msg)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`footer-service-${s.id}`}
                                        className="text-sm text-white/70 hover:text-[#f45100] transition-colors"
                                    >
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Cities */}
                    <div>
                        <h4 className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-[#f45100] mb-5">
                            Atendemos também
                        </h4>
                        <ul className="space-y-2.5">
                            <li className="text-sm text-white font-bold">
                                Praia Grande/SP
                            </li>
                            {NEIGHBOR_CITIES.map((c, i) => (
                                <li
                                    key={i}
                                    className="text-sm text-white/70"
                                >
                                    {c}/SP
                                </li>
                            ))}
                        </ul>

                        <div className="mt-6 p-4 bg-[#f45100]/10 border border-[#f45100]/30">
                            <div className="text-xs font-bold uppercase tracking-wider text-[#f45100] mb-1">
                                Emergência?
                            </div>
                            <a
                                href={COMPANY.phoneTel}
                                className="text-base font-extrabold hover:text-[#f45100]"
                            >
                                {COMPANY.phone}
                            </a>
                        </div>
                    </div>
                </div>

                {/* SEO local keywords block */}
                <div className="mt-14 pt-8 border-t border-white/10">
                    <div className="text-xs text-white/40 leading-relaxed">
                        <strong className="text-white/60">
                            Palavras-chave atendidas:
                        </strong>{" "}
                        guincho em praia grande, guincho 24 horas praia grande,
                        guincho perto de mim, reboque praia grande, guincho
                        urgente praia grande, socorro 24 horas praia grande,
                        guincho para moto praia grande, guincho barato praia
                        grande, auto socorro praia grande, guincho para
                        blindados, troca de pneu praia grande, recarga de
                        bateria praia grande, guincho para SUV, guincho para
                        carro importado, guincho na baixada santista.
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/50">
                    <div>
                        © {year} {COMPANY.name}. Todos os direitos reservados.
                    </div>
                    <div>
                        Site otimizado para SEO local · Praia Grande/SP
                    </div>
                </div>
            </div>
        </footer>
    );
};
